"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigurationJsonUtils = exports.articleTypeToConfigurationJsonKey = void 0;
const semver_1 = require("semver");
const echain_product_models_1 = require("@igus/echain-product-models");
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const ManagedConfigurationType_1 = require("./ManagedConfigurationType");
const echain_shared_i18n_1 = require("@igus/echain-shared-i18n");
exports.articleTypeToConfigurationJsonKey = {
    [echain_product_models_1.ArticleType.ae]: 'AEs',
    [echain_product_models_1.ArticleType.ia]: 'IAs',
    [echain_product_models_1.ArticleType.kette]: 'Kette',
    [echain_product_models_1.ArticleType.espool]: 'Espool',
    [echain_product_models_1.ArticleType.leitung]: 'Leitungen',
    [echain_product_models_1.ArticleType.link]: undefined,
    [echain_product_models_1.ArticleType.konfektionierung]: 'Konfektionierungen',
    [echain_product_models_1.ArticleType.fuehrungsrinne]: 'Fuehrungsrinnen',
    [echain_product_models_1.ArticleType.montageSet]: 'MontageSets',
    [echain_product_models_1.ArticleType.montBes]: 'MontBes',
    [echain_product_models_1.ArticleType.verpackung]: 'Verpackungen',
    [echain_product_models_1.ArticleType.zugentlastung]: 'Zugentlastungen',
    [echain_product_models_1.ArticleType.zubehoer]: 'Zubehoer',
};
/**
 * The `ConfigurationJsonUtils` provides low-level functions on `ConfigurationJson` objects.
 */
class ConfigurationJsonUtils {
    static clearArticles(configurationJson) {
        return Object.assign(Object.assign(Object.assign({}, configurationJson), { Kette: undefined, Espool: undefined }), ConfigurationJsonUtils.getBasicConfigurationJson());
    }
    static getBasicConfigurationJson() {
        return {
            AEs: [],
            Fuehrungsrinnen: [],
            IAs: [],
            Konfektionierungen: [],
            Leitungen: [],
            MontageSets: [],
            MontBes: [],
            Verpackungen: [],
            Zugentlastungen: [],
        };
    }
    /**
     * Adds an `ArticleJson` to a `ConfigurationJson`.
     */
    static addArticleByType(configurationJson, article, articleType) {
        ConfigurationJsonUtils.addArticle(configurationJson, Object.assign(Object.assign({}, article), { ArticleType: articleType }));
        return configurationJson;
    }
    /**
     * Adds an `ArticleJson` to a `ConfigurationJson`.
     */
    static addArticle(configurationJson, article) {
        const jsonKey = exports.articleTypeToConfigurationJsonKey[article.ArticleType];
        if (jsonKey === undefined) {
            throw new Error(`${this.className}.addArticle() cannot add ${article.ArticleType}`);
        }
        if (ConfigurationJsonUtils.isArticleArrayProperty(article.ArticleType)) {
            if (!Array.isArray(configurationJson[jsonKey])) {
                configurationJson[jsonKey] = [];
            }
            configurationJson[jsonKey].push(article);
            return configurationJson;
        }
        configurationJson[jsonKey] = article;
        return configurationJson;
    }
    /**
     * Retrieves whether an articleType is held inside an array.
     */
    static isArticleArrayProperty(articleType) {
        switch (articleType) {
            case echain_product_models_1.ArticleType.kette:
            case echain_product_models_1.ArticleType.espool:
            case echain_product_models_1.ArticleType.link:
                return false;
            case echain_product_models_1.ArticleType.ae:
            case echain_product_models_1.ArticleType.fuehrungsrinne:
            case echain_product_models_1.ArticleType.ia:
            case echain_product_models_1.ArticleType.konfektionierung:
            case echain_product_models_1.ArticleType.leitung:
            case echain_product_models_1.ArticleType.montageSet:
            case echain_product_models_1.ArticleType.montBes:
            case echain_product_models_1.ArticleType.verpackung:
            case echain_product_models_1.ArticleType.zugentlastung:
            case echain_product_models_1.ArticleType.zubehoer:
                return true;
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(articleType);
        }
    }
    /**
     * Retrieves all articles of an `ArticleType`
     */
    static getArticlesByType(configurationJson, articleType) {
        const jsonKey = exports.articleTypeToConfigurationJsonKey[articleType];
        if (jsonKey === undefined) {
            return [];
        }
        const articleOrArticles = configurationJson[jsonKey];
        const articles = articleOrArticles
            ? Array.isArray(articleOrArticles)
                ? articleOrArticles
                : [articleOrArticles]
            : [];
        // patch ArticleType into json
        return articles.map((a) => (Object.assign(Object.assign({}, a), { ArticleType: articleType })));
    }
    static getAllArticleIds(...configurationJsons) {
        const allArticleIds = echain_shared_articles_oo_1.ArticleUtils.createEmptyAllArticleIds();
        configurationJsons.forEach((configurationJson) => {
            var _a, _b;
            (configurationJson.AEs || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.ae.add(json.Id);
            });
            if ((_a = configurationJson.Espool) === null || _a === void 0 ? void 0 : _a.Id) {
                allArticleIds.espool.add(configurationJson.Espool.Id);
            }
            (configurationJson.Fuehrungsrinnen || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.fuehrungsrinne.add(json.Id);
            });
            (configurationJson.IAs || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.ia.add(json.Id);
            });
            if ((_b = configurationJson.Kette) === null || _b === void 0 ? void 0 : _b.Id) {
                allArticleIds.kette.add(configurationJson.Kette.Id);
            }
            (configurationJson.Konfektionierungen || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.konfektionierung.add(json.Id);
            });
            (configurationJson.Leitungen || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.leitung.add(json.Id);
            });
            (configurationJson.MontageSets || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.montageSet.add(json.Id);
            });
            (configurationJson.MontBes || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.montBes.add(json.Id);
            });
            (configurationJson.Verpackungen || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.verpackung.add(json.Id);
            });
            (configurationJson.Zubehoer || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.zubehoer.add(json.Id);
            });
            (configurationJson.Zugentlastungen || []).forEach((json) => {
                if (json.Id)
                    allArticleIds.zugentlastung.add(json.Id);
            });
        });
        return allArticleIds;
    }
    static getAllArticleArtNrs(...configurationJsons) {
        const allArticleArtNrs = echain_shared_articles_oo_1.ArticleUtils.createEmptyAllArticleArtNrs();
        configurationJsons.forEach((configurationJson) => {
            var _a, _b;
            (configurationJson.AEs || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.ae.add(JSON.stringify(json.ArtNr));
            });
            if ((_a = configurationJson.Espool) === null || _a === void 0 ? void 0 : _a.ArtNr) {
                allArticleArtNrs.espool.add(JSON.stringify(configurationJson.Espool.ArtNr));
            }
            (configurationJson.Fuehrungsrinnen || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.fuehrungsrinne.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.IAs || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.ia.add(JSON.stringify(json.ArtNr));
            });
            const ketteArtNr = (_b = configurationJson.Kette) === null || _b === void 0 ? void 0 : _b.ArtNr;
            if ((0, echain_shared_i18n_1.isLocalizedText)(ketteArtNr)) {
                allArticleArtNrs.kette.add(JSON.stringify(ketteArtNr));
            }
            (configurationJson.Konfektionierungen || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.konfektionierung.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.Leitungen || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.leitung.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.MontageSets || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.montageSet.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.MontBes || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.montBes.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.Verpackungen || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.verpackung.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.Zubehoer || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.zubehoer.add(JSON.stringify(json.ArtNr));
            });
            (configurationJson.Zugentlastungen || []).forEach((json) => {
                if ((0, echain_shared_i18n_1.isLocalizedText)(json.ArtNr))
                    allArticleArtNrs.zugentlastung.add(JSON.stringify(json.ArtNr));
            });
        });
        return allArticleArtNrs;
    }
    /**
     * check if a given source is a `ConfigurationJson`
     */
    static isConfigurationJson(source) {
        return Boolean(source && 'ManagedConfigurationType' in source);
    }
    /**
     * check if a given source is a `ConfigurationJson` and of `ManagedConfigurationType.Expert`
     */
    static isExpertConfigurationJson(source) {
        return this.isConfigurationJson(source) && source.ManagedConfigurationType === ManagedConfigurationType_1.ManagedConfigurationType.Expert;
    }
    /**
     * check if a given source is a `ConfigurationJson` and of `ManagedConfigurationType.IaConfigurator`
     */
    static isIAConfigurationJson(source) {
        return (this.isConfigurationJson(source) && source.ManagedConfigurationType === ManagedConfigurationType_1.ManagedConfigurationType.IaConfigurator);
    }
    /**
     * check if a given source is a `ConfigurationJson` and of `ManagedConfigurationType.Expert` using metric artNrs
     */
    static isMetricLegacyExpertConfigurationJson(source) {
        return (this.isExpertConfigurationJson(source) &&
            !(source.DomainVersion && (0, semver_1.valid)(source.DomainVersion) && (0, semver_1.gt)(source.DomainVersion, '61.7.0')));
    }
    /**
     * Checks if given `ConfigurationJson` semantically equals or not.
     *
     * "Semantic" means any kind of data which changes the content of the `Configuration`,
     * be it any user input value, changed articles or
     * meta information which possibly leads to different states in any kind of applications.
     *
     * By default, we remove any nully values and any `EntityIds`.
     */
    static equals(a, b, options = {
        removeProps: ['EntityId'],
        removeNully: true,
    }) {
        const aClone = (0, echain_shared_ts_utils_1.clonePrimitive)(a, options);
        const bClone = (0, echain_shared_ts_utils_1.clonePrimitive)(b, options);
        return (0, echain_shared_ts_utils_1.deepEqual)(aClone, bClone);
    }
}
exports.ConfigurationJsonUtils = ConfigurationJsonUtils;
ConfigurationJsonUtils.className = 'ConfigurationJsonUtils';
//# sourceMappingURL=ConfigurationJsonUtils.js.map